import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';

const MediaAndPromotionsData = ({ period, condensedIncomeData: externalData, condensedIncomeDataPrev: externalDataPrev }) => {
    const { apiFetch } = useAuth();
    const [promotionsData, setPromotionsData] = useState([]);
    const [mediaCounts, setMediaCounts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Internal state only used when data isn't passed as props
    const [internalData, setInternalData] = useState(null);
    const [internalDataPrev, setInternalDataPrev] = useState(null);

    // Use external data if available, otherwise use internally fetched data
    const condensedIncomeData = externalData ?? internalData;
    const condensedIncomeDataPrev = externalDataPrev ?? internalDataPrev;

    function getMonthYear(period) {
        if (!period) return { monthName: '', year: '', prevYear: '' };
        const periodStr = period.toString().padStart(6, '0');
        if (periodStr.length !== 6) return { monthName: '', year: '', prevYear: '' };
        const year = periodStr.substring(0, 4);
        const prevYear = (parseInt(year, 10) - 1).toString();
        const month = periodStr.substring(4, 6);
        const monthNames = [
            '', 'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const monthIndex = parseInt(month, 10);
        return { monthName: monthNames[monthIndex], year, prevYear };
    }

    const { monthName, year, prevYear } = getMonthYear(period);

    // Fetch condensed income data only when not provided via props
    useEffect(() => {
        if (!period || externalData) return;
        let isCancelled = false;

        const fetchCondensedData = async () => {
            try {
                const response = await apiFetch(`/api/Mda/CondensedIncomeStatementData?Period=${period}`);
                const data = await response.json();
                if (!isCancelled && data.success) {
                    setInternalData(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };

        fetchCondensedData();
        return () => { isCancelled = true; };
    }, [apiFetch, period, externalData]);

    useEffect(() => {
        if (!period || externalDataPrev) return;
        let isCancelled = false;

        const fetchPrevData = async () => {
            try {
                const { prevYear } = getMonthYear(period);
                const response = await apiFetch(`/api/Mda/CondensedIncomeStatementData?Period=${prevYear}12`);
                const data = await response.json();
                if (!isCancelled && data.success) {
                    setInternalDataPrev(data.data);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };

        fetchPrevData();
        return () => { isCancelled = true; };
    }, [apiFetch, period, externalDataPrev]);

    const sumByTransactionCode = (data, transactionCode) => {
        if (!data || !Array.isArray(data)) return null;
        const filtered = data.filter(item => item.TransactionCode === transactionCode);
        const toNum = (val) => {
            if (typeof val === 'number') return val;
            const parsed = parseFloat(val || '0');
            return isNaN(parsed) ? 0 : parsed;
        };
        return filtered.reduce(
            (totals, item) => {
                totals.CurrentBal += toNum(item.CurrentBal);
                totals.CurrentMovements += toNum(item.CurrentMovements);
                totals.BudgetFullYearRevised += toNum(item.BudgetFullYearRevised);
                return totals;
            },
            { CurrentBal: 0, CurrentMovements: 0, BudgetFullYearRevised: 0 }
        );
    };

    // Fetch counts data (still from Marketing endpoints)
    useEffect(() => {
        if (!period) return;
        let isCancelled = false;

        const fetchCountsData = async () => {
            try {
                const promotionsResponse = await apiFetch(`/api/Marketing/GetPromotionsCounts?Period=${period}`);
                const promotionsDataResponse = await promotionsResponse.json();

                const mediaCountsResponse = await apiFetch(`/api/Marketing/GetMediaCounts?Period=${period}`);
                const mediaCountsData = await mediaCountsResponse.json();

                if (isCancelled) return;

                if (promotionsDataResponse.success) {
                    setPromotionsData(promotionsDataResponse.data);
                }
                if (mediaCountsData.success) {
                    setMediaCounts(mediaCountsData.data);
                }
                setIsLoading(false);
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
                if (!isCancelled) setIsLoading(false);
            }
        };

        fetchCountsData();
        return () => { isCancelled = true; };
    }, [apiFetch, period]);

    // Derive income values from condensed income data using TransactionCode
    // 192 = Promotions Income, 104 = Media Income
    const getIncomeValues = (transactionCode) => {
        const current = sumByTransactionCode(condensedIncomeData, transactionCode);
        const prev = sumByTransactionCode(condensedIncomeDataPrev, transactionCode);
        return {
            month: current ? current.CurrentMovements * -1 : 0,
            ytd: current ? current.CurrentBal * -1 : 0,
            prev: prev ? prev.BudgetFullYearRevised * -1 : 0
        };
    };

    const promotionsIncome = getIncomeValues(192);
    const mediaIncome = getIncomeValues(104);

    const formatCurrency = (value) => {
        if (typeof value !== 'number') value = Number(value) || 0;
        let formatted = new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'ZAR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
        formatted = formatted.replace(/\s?ZAR$/, '').trim();
        formatted = formatted.replace(/\u202F/g, '  ');
        return `R ${formatted}`;
    };

    function formatNumberWithSpace(value) {
        if (typeof value !== 'number') value = Number(value) || 0;
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    const incomeDataReady = condensedIncomeData && condensedIncomeDataPrev;

    if (isLoading || !incomeDataReady) {
        return <div>Loading media and promotions data...</div>;
    }

    return (
        <table>
            <thead>
                <tr>
                    <th>Media and Promotions</th>
                    <th>
                        Month<br />
                        {monthName} {year}
                    </th>
                    <th>
                        Year to Date<br />
                        {monthName} {year}
                    </th>
                    <th>
                        Year Ended<br />
                        December {prevYear}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Number of promotions booked</td>
                    <td>{formatNumberWithSpace(promotionsData[0])}</td>
                    <td>{formatNumberWithSpace(promotionsData[1])}</td>
                    <td>{formatNumberWithSpace(promotionsData[2])}</td>
                </tr>
                <tr>
                    <td>Promotions income generated (R)</td>
                    <td>{formatCurrency(promotionsIncome.month)}</td>
                    <td>{formatCurrency(promotionsIncome.ytd)}</td>
                    <td>{formatCurrency(promotionsIncome.prev)}</td>
                </tr>
                <tr>
                    <td>Number of media bookings</td>
                    <td>{formatNumberWithSpace(mediaCounts[0])}</td>
                    <td>{formatNumberWithSpace(mediaCounts[1])}</td>
                    <td>{formatNumberWithSpace(mediaCounts[2])}</td>
                </tr>
                <tr>
                    <td>Media income generated (R)</td>
                    <td>{formatCurrency(mediaIncome.month)}</td>
                    <td>{formatCurrency(mediaIncome.ytd)}</td>
                    <td>{formatCurrency(mediaIncome.prev)}</td>
                </tr>
            </tbody>
        </table>
    );
};

export default MediaAndPromotionsData;